/**
 * MessageTemplateViewModal - Модальное окно просмотра шаблона
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Box,
  Button,
} from '@mantine/core';
import {
  IconTemplate,
  IconPencil,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconUser,
  IconCalendar,
} from '@tabler/icons-react';
import tokens from '../../theme/designTokens';

/**
 * Иконки для каналов
 */
const CHANNEL_ICONS = {
  whatsapp: { icon: IconBrandWhatsapp, color: 'green', label: 'WhatsApp' },
  telegram: { icon: IconBrandTelegram, color: 'blue', label: 'Telegram' },
  viber: { icon: IconPhone, color: 'grape', label: 'Viber' },
  vk: { icon: IconBrandVk, color: 'indigo', label: 'VK' },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Компонент для отображения поля
 */
const InfoField = ({ icon: Icon, label, value, children }) => {
  if (!value && !children) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {children || <Text size="sm">{value}</Text>}
      </Box>
    </Group>
  );
};

const MessageTemplateViewModal = ({
  opened,
  onClose,
  template,
  onEdit,
}) => {
  if (!template) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Шаблон сообщения"
      size="md"
      radius="lg"
    >
      <Stack gap="lg">
        {/* Заголовок с каналами */}
        <Group>
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              backgroundColor: tokens.colors.brand.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconTemplate size={28} color={tokens.colors.brand.primary} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              {template.channels.map((channel) => {
                const channelInfo = CHANNEL_ICONS[channel];
                if (!channelInfo) return null;
                const Icon = channelInfo.icon;
                return (
                  <Badge
                    key={channel}
                    variant="light"
                    color={channelInfo.color}
                    leftSection={<Icon size={12} />}
                    size="md"
                    radius="xl"
                  >
                    {channelInfo.label}
                  </Badge>
                );
              })}
            </Group>
            <Text size="sm" c="dimmed" mt={4}>
              Шаблон #{template.id}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Информация */}
        <Stack gap="md">
          <InfoField icon={IconUser} label="Создан">
            <Text size="sm">
              {template.created_by?.full_name || template.created_by?.email || 'Не указан'}
            </Text>
          </InfoField>

          <InfoField
            icon={IconCalendar}
            label="Дата создания"
            value={formatDate(template.created_at)}
          />

          {template.updated_at !== template.created_at && (
            <InfoField
              icon={IconCalendar}
              label="Дата обновления"
              value={formatDate(template.updated_at)}
            />
          )}
        </Stack>

        <Divider />

        {/* Текст сообщения */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Текст сообщения
          </Text>
          <Box
            p="md"
            style={{
              backgroundColor: tokens.colors.neutral[1],
              borderRadius: '8px',
            }}
          >
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {template.message_text}
            </Text>
          </Box>
        </Stack>

        <Divider />

        {/* Кнопки */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => onEdit(template)}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default MessageTemplateViewModal;
